import { useEffect, useRef, useState } from "react";
import { useSession } from "@/auth/SessionContext";
import { api } from "@/lib/api";
import { formatWhen } from "@/lib/format";
import type {
  ProjectAlert,
  ProjectAlertReason,
  ProjectAlertsPage,
  ProjectAlertStatus,
} from "@/lib/types";
import { Button } from "@/components/Button";
import { StatePanel } from "@/components/StatePanel";
import { StatusRail } from "@/components/StatusRail";
import type { Locale } from "@/locale";
import type { Messages } from "@/locale/types";
import { useLocale } from "@/locale/LocaleContext";
import ui from "@/components/ui.module.css";
import styles from "@/pages/app/alerts.module.css";

function reasonLabel(m: Messages, reason: ProjectAlertReason | string): string {
  if (reason === "degraded") return m.projects.alertsReasonDegraded;
  if (reason === "unavailable") return m.projects.alertsReasonUnavailable;
  if (reason === "stale") return m.projects.alertsReasonStale;
  return m.projects.servicesUnknown;
}

function statusLabel(m: Messages, status: ProjectAlertStatus | string): string {
  if (status === "open") return m.projects.alertsStatusOpen;
  if (status === "resolved") return m.projects.alertsStatusResolved;
  return m.projects.servicesUnknown;
}

function alertRail(status: ProjectAlertStatus, reason: ProjectAlertReason): string {
  if (status === "resolved") return "succeeded";
  if (reason === "unavailable") return "failed";
  if (reason === "stale") return "stale";
  return "running";
}

type Props = {
  projectId: string;
};

export function ProjectAlertsPanel({ projectId }: Props) {
  const { m, locale, fmt } = useLocale();
  const { session } = useSession();
  const emailDelivery = session?.features.email_delivery === true;
  const [page, setPage] = useState<ProjectAlertsPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retry, setRetry] = useState(0);
  const [prefBusy, setPrefBusy] = useState(false);
  const [prefError, setPrefError] = useState<string | null>(null);
  const [ackBusyId, setAckBusyId] = useState<string | null>(null);
  const [ackError, setAckError] = useState<string | null>(null);
  const projectIdRef = useRef(projectId);
  const failRef = useRef(m.projects.alertsFailed);
  projectIdRef.current = projectId;
  failRef.current = m.projects.alertsFailed;

  useEffect(() => {
    setPage(null);
    setError(null);
    setLoading(true);
    setPrefError(null);
    setAckError(null);
    setPrefBusy(false);
    setAckBusyId(null);
  }, [projectId]);

  useEffect(() => {
    let alive = true;
    const ac = new AbortController();
    const captured = projectId;
    setLoading(true);
    setError(null);
    api
      .projectAlerts(captured, ac.signal)
      .then((res) => {
        if (!alive || projectIdRef.current !== captured) return;
        setPage(res);
        setError(null);
      })
      .catch((e) => {
        if (!alive || ac.signal.aborted) return;
        if (projectIdRef.current !== captured) return;
        setPage(null);
        setError(e instanceof Error ? e.message : failRef.current);
      })
      .finally(() => {
        if (alive && projectIdRef.current === captured) setLoading(false);
      });
    return () => {
      alive = false;
      ac.abort();
    };
  }, [projectId, retry]);

  async function refetchAlerts(captured: string): Promise<boolean> {
    try {
      const res = await api.projectAlerts(captured);
      if (projectIdRef.current !== captured) return false;
      setPage(res);
      setError(null);
      return true;
    } catch (e) {
      if (projectIdRef.current !== captured) return false;
      setError(e instanceof Error ? e.message : failRef.current);
      return false;
    }
  }

  async function onSetEnabled(next: boolean) {
    if (prefBusy || ackBusyId) return;
    const captured = projectId;
    setPrefBusy(true);
    setPrefError(null);
    setAckError(null);
    try {
      const res = await api.setProjectAlertPreferences(captured, next);
      if (projectIdRef.current !== captured) return;
      // Apply server preference, then refetch list so history/status stay authoritative.
      setPage((prev) => (prev ? { ...prev, enabled: res.enabled } : prev));
      await refetchAlerts(captured);
    } catch (e) {
      if (projectIdRef.current !== captured) return;
      setPrefError(e instanceof Error ? e.message : m.projects.alertsPrefFailed);
    } finally {
      if (projectIdRef.current === captured) setPrefBusy(false);
    }
  }

  async function onAcknowledge(alertId: string) {
    if (prefBusy || ackBusyId) return;
    const captured = projectId;
    setAckBusyId(alertId);
    setAckError(null);
    setPrefError(null);
    try {
      const res = await api.acknowledgeProjectAlert(captured, alertId);
      if (projectIdRef.current !== captured) return;
      // Only mark acknowledged after server returns acknowledged_at — no optimistic fake ack.
      setPage((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          items: prev.items.map((item) =>
            item.id === res.id ? { ...item, acknowledged_at: res.acknowledged_at } : item,
          ),
        };
      });
    } catch (e) {
      if (projectIdRef.current !== captured) return;
      setAckError(e instanceof Error ? e.message : m.projects.alertsAckFailed);
    } finally {
      if (projectIdRef.current === captured) setAckBusyId(null);
    }
  }

  const displayPage = page;
  const mutating = prefBusy || ackBusyId != null;

  return (
    <section className={ui.panel} style={{ marginTop: "1.25rem" }} aria-labelledby="project-alerts">
      <h2 id="project-alerts" className="display" style={{ margin: 0, fontSize: "1.1rem" }}>
        {m.projects.alertsTitle}
      </h2>
      <p className={ui.meta} style={{ marginTop: "0.35rem" }}>
        {m.projects.alertsLead}
      </p>
      <p className={ui.meta} style={{ marginTop: "0.25rem" }}>
        {emailDelivery ? m.projects.alertsChannelReported : m.projects.alertsChannelNote}
      </p>

      <div className={styles.toolbar}>
        {displayPage ? (
          <p className={ui.meta} style={{ margin: 0 }}>
            {fmt(m.projects.alertsObserved, {
              when: formatWhen(displayPage.observed_at, locale),
            })}
          </p>
        ) : (
          <span />
        )}
        <Button
          type="button"
          variant="secondary"
          disabled={loading || mutating}
          aria-busy={loading || undefined}
          onClick={() => setRetry((n) => n + 1)}
        >
          {loading ? m.projects.alertsRefreshing : m.projects.alertsRefresh}
        </Button>
      </div>

      {loading && !displayPage ? <StatePanel kind="pending" title={m.common.loading} /> : null}

      {error ? (
        <div role="alert" style={{ marginTop: "0.75rem" }}>
          <p className={ui.fieldError}>{error}</p>
          <Button type="button" variant="secondary" onClick={() => setRetry((n) => n + 1)}>
            {m.projects.alertsRetry}
          </Button>
        </div>
      ) : null}

      {displayPage && !error ? (
        <div aria-busy={loading || mutating || undefined}>
          <div className={styles.prefRow}>
            <p className={styles.prefState}>
              {displayPage.enabled
                ? m.projects.alertsEnabledLabel
                : m.projects.alertsDisabledLabel}
            </p>
            {displayPage.enabled ? (
              <Button
                type="button"
                variant="secondary"
                disabled={mutating || loading}
                aria-busy={prefBusy || undefined}
                onClick={() => void onSetEnabled(false)}
              >
                {m.projects.alertsDisable}
              </Button>
            ) : (
              <Button
                type="button"
                variant="primary"
                disabled={mutating || loading}
                aria-busy={prefBusy || undefined}
                onClick={() => void onSetEnabled(true)}
              >
                {m.projects.alertsEnable}
              </Button>
            )}
          </div>

          {!displayPage.enabled ? (
            <p className={ui.meta} style={{ marginTop: "0.55rem" }}>
              {m.projects.alertsDisabledNote}
            </p>
          ) : null}

          {prefError ? (
            <p className={`${ui.fieldError} ${styles.mutationError}`} role="alert">
              {prefError}
            </p>
          ) : null}

          {ackError ? (
            <p className={`${ui.fieldError} ${styles.mutationError}`} role="alert">
              {ackError}
            </p>
          ) : null}

          <p className={ui.meta} style={{ marginTop: "0.65rem" }}>
            {m.projects.alertsAckNote}
          </p>

          {displayPage.items.length === 0 ? (
            <StatePanel
              kind="empty"
              title={m.projects.alertsEmpty}
              message={m.projects.alertsEmptyBody}
            />
          ) : (
            <ul className={styles.list}>
              {displayPage.items.map((alert) => (
                <AlertItem
                  key={alert.id}
                  alert={alert}
                  locale={locale}
                  busy={ackBusyId === alert.id}
                  disabled={mutating || loading}
                  onAcknowledge={() => void onAcknowledge(alert.id)}
                />
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </section>
  );
}

function AlertItem({
  alert,
  locale,
  busy,
  disabled,
  onAcknowledge,
}: {
  alert: ProjectAlert;
  locale: Locale;
  busy: boolean;
  disabled: boolean;
  onAcknowledge: () => void;
}) {
  const { m, fmt } = useLocale();
  const canAck = alert.acknowledged_at == null;
  const open = alert.status === "open";

  return (
    <li className={[styles.item, open ? styles.itemOpen : ""].filter(Boolean).join(" ")}>
      <StatusRail state={alertRail(alert.status, alert.reason)} />
      <div className={styles.itemBody}>
        <div className={styles.itemHead}>
          <span className={styles.serviceName}>{alert.service_name}</span>
          <span className={ui.meta}>
            {statusLabel(m, alert.status)} · {reasonLabel(m, alert.reason)}
          </span>
        </div>
        <p className={`mono ${styles.metaLine}`}>
          {fmt(m.projects.alertsOpened, {
            when: formatWhen(alert.opened_at, locale),
          })}
        </p>
        {alert.resolved_at ? (
          <p className={`mono ${styles.metaLine}`}>
            {fmt(m.projects.alertsResolved, {
              when: formatWhen(alert.resolved_at, locale),
            })}
          </p>
        ) : null}
        {alert.acknowledged_at ? (
          <p className={`mono ${styles.metaLine}`}>
            {fmt(m.projects.alertsAcknowledged, {
              when: formatWhen(alert.acknowledged_at, locale),
            })}
          </p>
        ) : null}
        {canAck ? (
          <div className={styles.actions}>
            <Button
              type="button"
              variant="secondary"
              disabled={disabled}
              aria-busy={busy || undefined}
              onClick={onAcknowledge}
            >
              {busy ? m.projects.alertsAcknowledging : m.projects.alertsAcknowledge}
            </Button>
          </div>
        ) : null}
      </div>
    </li>
  );
}
